/**
 * 旅游外链渠道 code（广发 / 平安 / 交通银行 / 云闪付积点），与 0息游 `travelChannelCode` storage 对齐。
 */

/** 本地 storage 键（供请求头 X-Channel-Code） */
export const TRAVEL_CHANNEL_CODE_STORAGE_KEY = 'travelChannelCode' as const

/** 落地页 query 名 */
export const TRAVEL_BANK_CODE_QUERY_KEY = 'bankCode' as const

/** 吉乐游 / 0息游外跳入站 query `source`（与 shopJointLogin `WUJINBAO_ENTRY_SOURCE` 一致） */
export const TRAVEL_JILEYOU_ENTRY_SOURCE = 'wujinbao' as const

/** 外链渠道 code 枚举 */
export const TRAVEL_CHANNEL_CODE = {
  GDB_TRAVEL: 'GDB_TRAVEL',
  PAB_TRAVEL: 'PAB_TRAVEL',
  BCM_TRAVEL: 'BCM_TRAVEL',
  CLOUD_POINT_TRAVEL: 'CLOUD_POINT_TRAVEL',
} as const

const KNOWN_TRAVEL_CHANNEL_CODES = new Set<string>(Object.values(TRAVEL_CHANNEL_CODE))

function normalizeQueryValue(raw: string | undefined): string {
  if (raw == null || raw === '')
    return ''
  try {
    return decodeURIComponent(String(raw)).trim()
  }
  catch {
    return String(raw).trim()
  }
}

function normalizeBankCodeQueryValue(raw: string | undefined): string {
  return normalizeQueryValue(raw)
}

function isKnownTravelChannelCode(value: string): boolean {
  return KNOWN_TRAVEL_CHANNEL_CODES.has(value)
}

/** 读取本地渠道 code；无值或失败时返回空字符串 */
export function readTravelChannelCodeFromStorage(): string {
  try {
    return String(uni.getStorageSync(TRAVEL_CHANNEL_CODE_STORAGE_KEY) ?? '').trim()
  }
  catch {
    return ''
  }
}

/** 清除本地旅游外链渠道 code */
export function clearTravelChannelCodeFromStorage(): void {
  try {
    uni.removeStorageSync(TRAVEL_CHANNEL_CODE_STORAGE_KEY)
  }
  catch {
    // ignore
  }
}

/**
 * 写入渠道 code（与已存不一致时覆盖）；仅接受已知枚举值。
 */
export function persistTravelChannelCode(value: string | undefined): boolean {
  const trimmed = (value ?? '').trim()
  if (!trimmed || !isKnownTravelChannelCode(trimmed))
    return false
  try {
    uni.setStorageSync(TRAVEL_CHANNEL_CODE_STORAGE_KEY, trimmed)
    return true
  }
  catch {
    return false
  }
}

/**
 * 页面 / 路由 query 的 `bankCode`：有合法值则写入/覆盖；无参或空值不改动本地（不主动清理）。
 * 吉乐游外跳落地请用 {@link syncTravelChannelCodeFromPageOptions}。
 */
export function persistTravelChannelCodeFromQuery(
  options: Record<string, string | undefined> | undefined,
): void {
  if (options == null || !(TRAVEL_BANK_CODE_QUERY_KEY in options))
    return
  const normalized = normalizeBankCodeQueryValue(options[TRAVEL_BANK_CODE_QUERY_KEY])
  if (!normalized)
    return
  persistTravelChannelCode(normalized)
}

/**
 * 联登 / H5 路由落地：同步 `travelChannelCode`。
 * 吉乐游（`source=wujinbao`）且未传、空值或非法 `bankCode` 时清空本地，避免沿用历史频道。
 */
export function syncTravelChannelCodeFromPageOptions(
  options: Record<string, string | undefined> | undefined,
): void {
  const entrySource = normalizeQueryValue(options?.source)
  if (entrySource === TRAVEL_JILEYOU_ENTRY_SOURCE) {
    if (options == null || !(TRAVEL_BANK_CODE_QUERY_KEY in options)) {
      clearTravelChannelCodeFromStorage()
      return
    }
    const normalized = normalizeBankCodeQueryValue(options[TRAVEL_BANK_CODE_QUERY_KEY])
    if (!normalized || !persistTravelChannelCode(normalized))
      clearTravelChannelCodeFromStorage()
    return
  }
  persistTravelChannelCodeFromQuery(options)
}
